/**
 * useReconnect Hook
 *
 * Handles OAuth re-authentication flow from widget context.
 * Uses popup with postMessage communication, falls back to redirect with user consent.
 *
 * @see .work/specs/M9/005-widget-platform-integration.md
 */
'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { apiClient } from '@/lib/api';
import {
  openProviderPopup,
  OAuthErrorCode,
  type PopupResultError,
} from '@/src/client/oauth/start-oauth';

import { usePlatformErrors } from '../context';
import type { OAuthProvider } from '../types';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const OAUTH_RETURN_KEY = 'appgnostic-oauth-return';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface ReconnectOptions {
  accountId: string;
  provider: OAuthProvider;
}

export interface UseReconnectReturn {
  /** Initiate reconnect flow for an account */
  reconnect: (options: ReconnectOptions) => Promise<void>;
  /** True while reconnection is in progress */
  isReconnecting: boolean;
  /** Account ID currently being reconnected */
  reconnectingAccountId: string | null;
}

interface OAuthReturnState {
  returnUrl: string;
  accountId: string;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/**
 * Validates that a URL is same-origin for security.
 */
function isSameOrigin(url: string): boolean {
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Shows a confirmation dialog for redirect fallback.
 * Uses browser confirm() for simplicity; can be upgraded to custom dialog.
 */
function showRedirectConfirmation(options: {
  title: string;
  message: string;
}): boolean {
  return window.confirm(`${options.title}\n\n${options.message}`);
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

/**
 * Hook for initiating OAuth reconnection from widget context.
 *
 * Handles popup flow with fallback to redirect when popups are blocked.
 * Automatically clears platform errors and invalidates queries on success.
 *
 * @example
 * function ReconnectButton({ accountId, provider }) {
 *   const { reconnect, isReconnecting } = useReconnect();
 *
 *   return (
 *     <Button
 *       onClick={() => reconnect({ accountId, provider })}
 *       disabled={isReconnecting}
 *     >
 *       {isReconnecting ? 'Reconnecting...' : 'Reconnect'}
 *     </Button>
 *   );
 * }
 */
export function useReconnect(): UseReconnectReturn {
  const queryClient = useQueryClient();
  const { clearError } = usePlatformErrors();

  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reconnectingAccountId, setReconnectingAccountId] = useState<
    string | null
  >(null);

  const reconnect = useCallback(
    async ({ accountId, provider }: ReconnectOptions): Promise<void> => {
      setIsReconnecting(true);
      setReconnectingAccountId(accountId);

      try {
        await openProviderPopup({
          provider,
          onSuccess: () => {
            // Clear the platform error for this account
            clearError(accountId);

            // Invalidate queries to refetch with new token
            queryClient.invalidateQueries({
              queryKey: ['provider-accounts'],
            });

            setIsReconnecting(false);
            setReconnectingAccountId(null);
          },
          onTicket: async ticket => {
            // Consume the ticket to update the token in the database.
            // The account already exists, so no user confirmation needed.
            try {
              await apiClient.post(`/api/integrations/${provider}/link`, {
                ticketId: ticket.ticketId,
              });
            } catch (err) {
              console.error('Failed to link account after reconnect:', err);
            }

            clearError(accountId);

            queryClient.invalidateQueries({
              queryKey: ['provider-accounts'],
            });

            setIsReconnecting(false);
            setReconnectingAccountId(null);
          },
          onError: (error: PopupResultError) => {
            // Log error but don't throw - let the user retry
            console.error('OAuth reconnect failed:', error);

            setIsReconnecting(false);
            setReconnectingAccountId(null);
          },
        });
      } catch (error) {
        // Popup was blocked - offer redirect fallback
        if (
          error instanceof Error &&
          'code' in error &&
          (error as { code: string }).code === OAuthErrorCode.POPUP_BLOCKED
        ) {
          const confirmed = showRedirectConfirmation({
            title: 'Popup Blocked',
            message:
              'Your browser blocked the authentication popup. Would you like to continue in this window? You will be redirected back after authentication.',
          });

          if (confirmed) {
            // Store return state for post-redirect restoration
            const returnState: OAuthReturnState = {
              returnUrl: window.location.href,
              accountId,
            };
            sessionStorage.setItem(
              OAUTH_RETURN_KEY,
              JSON.stringify(returnState)
            );

            // Redirect to OAuth start
            window.location.href = `/api/integrations/${provider}/oauth/start`;
            return;
          }
        }

        setIsReconnecting(false);
        setReconnectingAccountId(null);
      }
    },
    [clearError, queryClient]
  );

  return {
    reconnect,
    isReconnecting,
    reconnectingAccountId,
  };
}

// -----------------------------------------------------------------------------
// OAuth Return Handler
// -----------------------------------------------------------------------------

/**
 * Handle OAuth return after redirect-based authentication.
 *
 * Call this on app mount to process redirect returns.
 * Clears stored state and invalidates queries.
 *
 * @example
 * // In Providers component
 * useEffect(() => {
 *   handleOAuthReturn(queryClient, (message) => {
 *     toast.success(message);
 *   });
 * }, [queryClient]);
 */
export function handleOAuthReturn(
  queryClient: ReturnType<typeof useQueryClient>,
  showSuccessToast?: (message: string) => void
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const oauthReturn = sessionStorage.getItem(OAUTH_RETURN_KEY);

  if (oauthReturn) {
    try {
      const { returnUrl, accountId } = JSON.parse(
        oauthReturn
      ) as OAuthReturnState;
      sessionStorage.removeItem(OAUTH_RETURN_KEY);

      // Validate return URL is same-origin
      if (returnUrl && !isSameOrigin(returnUrl)) {
        console.warn('OAuth return URL is not same-origin, ignoring');
        return;
      }

      // Invalidate queries to refetch with new token
      queryClient.invalidateQueries({
        queryKey: ['provider-accounts'],
      });

      // Show success notification
      showSuccessToast?.('Account reconnected successfully');

      // Note: We can't clear the platform error here because we don't have
      // access to the context outside of React. The error will clear on
      // next render when the token status check passes.
      void accountId; // Acknowledge unused - keeping for future use
    } catch {
      // Invalid stored data, just clean up
      sessionStorage.removeItem(OAUTH_RETURN_KEY);
    }
  }
}
